import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  analyzePresentationTemplate,
  deletePresentationBrandAsset,
  getPresentationBrand,
  getPresentationTemplateCatalog,
  getSkillEnabled,
  getSkillGuidance,
  type PresentationBrandResponse,
  type PresentationTemplateAnalysisResponse,
  type PresentationTemplateCatalogResponse,
  putPresentationBrand,
  replaceSkillEnabled,
  replaceSkillGuidance,
  type SkillEnabledMap,
  type SkillEnabledResponse,
  type SkillGuidanceMap,
  type SkillGuidanceResponse,
  uploadPresentationBrandAsset,
} from "@/api/admin/skills"

import { adminQueryKeys } from "../admin-query-keys"
import { GC_TIME, MEDIUM_STALE_TIME } from "./_shared"

// =============================================================================
// Skill guidance — global runtime overlays for artifact skills
// =============================================================================

export function useSkillGuidanceQuery(enabled = true) {
  return useQuery<SkillGuidanceResponse>({
    queryKey: adminQueryKeys.skillGuidance(),
    queryFn: getSkillGuidance,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useReplaceSkillGuidanceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      guidance,
      expectedHash,
      reason,
      source,
    }: {
      guidance: SkillGuidanceMap
      expectedHash: string
      reason: string
      source?: string
    }) => replaceSkillGuidance(guidance, { expectedHash, reason, source }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.skillGuidance() })
    },
  })
}

// =============================================================================
// Skill enable/disable — admin kill-switch per artifact skill
// =============================================================================

export function useSkillEnabledQuery(enabled = true) {
  return useQuery<SkillEnabledResponse>({
    queryKey: adminQueryKeys.skillEnabled(),
    queryFn: getSkillEnabled,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useReplaceSkillEnabledMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      enabled,
      expectedHash,
      reason,
      source,
    }: {
      enabled: SkillEnabledMap
      expectedHash: string
      reason: string
      source?: string
    }) => replaceSkillEnabled(enabled, { expectedHash, reason, source }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.skillEnabled() })
    },
  })
}

// =============================================================================
// Presentation template layout catalog
// =============================================================================

export function usePresentationBrandQuery(enabled = true) {
  return useQuery<PresentationBrandResponse>({
    queryKey: adminQueryKeys.presentationBrand(),
    queryFn: getPresentationBrand,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function usePutPresentationBrandMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: putPresentationBrand,
    onSuccess: (data) => {
      queryClient.setQueryData(adminQueryKeys.presentationBrand(), data)
    },
  })
}

export function useUploadPresentationBrandAssetMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      slotId,
      file,
      meta,
    }: {
      slotId: string
      file?: File
      meta: { filename: string; required: boolean; role: string }
    }) => uploadPresentationBrandAsset(slotId, file, meta),
    onSuccess: (data) => {
      queryClient.setQueryData(adminQueryKeys.presentationBrand(), data)
    },
  })
}

export function useDeletePresentationBrandAssetMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deletePresentationBrandAsset,
    onSuccess: (data) => {
      queryClient.setQueryData(adminQueryKeys.presentationBrand(), data)
    },
  })
}

export function usePresentationTemplateCatalogQuery(enabled = true) {
  return useQuery<PresentationTemplateCatalogResponse>({
    queryKey: adminQueryKeys.presentationTemplateCatalog(),
    queryFn: getPresentationTemplateCatalog,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useAnalyzePresentationTemplateMutation() {
  const queryClient = useQueryClient()
  return useMutation<PresentationTemplateAnalysisResponse>({
    mutationFn: analyzePresentationTemplate,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminQueryKeys.presentationTemplateCatalog(),
      })
    },
  })
}
